import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Modal,
  TextField,
  Breadcrumbs,
  Link
} from '@mui/material';
import { Domain, Description, MoreVert, Delete } from '@mui/icons-material';
import apiService from '../../services/apiService';
import OrganizationUsersCard from '../cards/OrganizationUsersCard';
import OrganizationRulesCard from '../cards/OrganizationRulesCard';
import ModelFundsCard from '../cards/ModelFundsCard';
import SessionStatisticsChart from '../SessionStatisticsChart';
import { useToast } from '../common/ToastProvider';
import '../styles/ContentComponents.css';

function OrganizationDetail() {
  const { showSuccess, showError } = useToast();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organizationOptionsModalOpen, setOrganizationOptionsModalOpen] = useState(false);
  const [deleteOrganizationModalOpen, setDeleteOrganizationModalOpen] = useState(false);
  const [deleteOrganizationLoading, setDeleteOrganizationLoading] = useState(false);
  const [deleteOrganizationConfirmation, setDeleteOrganizationConfirmation] = useState('');

  const fetchOrganizationDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchOrganizationById(organizationId);
      
      if (result.success) {
        setOrganization(result.data.organization);
        
        // If organization has a company_id, fetch the company details
        if (result.data.organization.company_id) {
          const companyResult = await apiService.fetchCompanyById(result.data.organization.company_id);
          if (companyResult.success) {
            setCompany(companyResult.data.company);
          }
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to load organization details');
    }
    
    setLoading(false);
  }, [organizationId]);

  useEffect(() => {
    fetchOrganizationDetail();
  }, [fetchOrganizationDetail]);



  const getDemoStatus = (demo) => {
    return demo ? 'Demo' : 'False';
  };

  const getSetupStatus = (needSetup) => {
    return needSetup ? 'Setup Required' : 'Setup Complete';
  };

  const formatToken = (token) => {
    if (!token) return 'No token';
    return token;
  };

  const getIndustryDisplay = (industry) => {
    if (!industry) return 'Not specified';
    return industry.charAt(0).toUpperCase() + industry.slice(1);
  };

  const getSubscriptionStatus = (company) => {
    if(!company.subscription_start_date) return 'No subscription';
    if (!company.subscription_end_date && company.subscription_start_date) return 'Free plan';
    
    const endDate = new Date(company.subscription_end_date);
    const now = new Date();
    
    if (endDate < now) return 'Expired';
    if (endDate.getTime() - now.getTime() < 7 * 24 * 60 * 60 * 1000) return 'Expiring Soon';
    return 'Active';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'success';
      case 'Expiring Soon':
        return 'warning';
      case 'Expired':
        return 'error';
      case 'Free plan':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleOrganizationOptionsClick = () => {
    setOrganizationOptionsModalOpen(true);
  };

  const handleOrganizationOptionsModalClose = () => {
    setOrganizationOptionsModalOpen(false);
  };

  const handleDeleteOrganizationClick = () => {
    setOrganizationOptionsModalOpen(false);
    setDeleteOrganizationConfirmation('');
    setDeleteOrganizationModalOpen(true);
  };

  const handleDeleteOrganizationModalClose = () => {
    setDeleteOrganizationModalOpen(false);
    setDeleteOrganizationConfirmation('');
  };

  const handleDeleteOrganizationConfirm = async () => {
    if (deleteOrganizationConfirmation !== organization.organization_name) {
      showError('Please enter the exact organization name to confirm deletion', 'Error');
      return;
    }

    setDeleteOrganizationLoading(true);

    try {
      const result = await apiService.deleteOrganization(organizationId);
      
      if (result.success) {
        console.log('Organization deleted successfully:', result.data);
        showSuccess(`Organization '${organization.organization_name}' and all related data deleted successfully`, 'Success');
        // Navigate back to organizations list
        navigate('/projects');
      } else {
        console.error('Failed to delete organization:', result.error);
        showError(`Failed to delete organization: ${result.error}`, 'Error');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      showError('Failed to delete organization. Please try again.', 'Error');
    } finally {
      setDeleteOrganizationLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Box>
    );
  }

  if (!organization) {
    return (
      <Box sx={{ width: '100vw', minHeight: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: 2 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>Organization not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 1, sm: 2 }, width: '100%' }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs sx={{ mb: 3, color: 'text.secondary' }}>
          <Link 
            href="/projects" 
            color="inherit" 
            underline="hover"
            sx={{ 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
            onClick={(e) => {
              e.preventDefault();
              navigate('/projects');
            }}
          >
            <Domain sx={{ fontSize: 16 }} />
            Projects
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Domain sx={{ fontSize: 16 }} />
            {organization.organization_name || 'Unnamed Project'}
          </Typography>
        </Breadcrumbs>
        
        {/* Page Header with Actions */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 4,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <Domain sx={{ flexShrink: 0 }} />
              {organization.organization_name || 'Unnamed Project'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1 
            }}>
              <Typography component="span" fontWeight={500}>Project ID:</Typography>
              <Typography 
                component="span" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  backgroundColor: 'grey.100',
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.300'
                }}
              >
                {organization.unique_id}
              </Typography>
            </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexShrink: 0,
            flexDirection: { xs: 'row', sm: 'row' }
          }}>
            <Button 
              variant="outlined" 
              startIcon={<MoreVert />}
              onClick={handleOrganizationOptionsClick}
              sx={{ 
                minWidth: 'auto',
                px: 2
              }}
            >
              Options
            </Button>
          </Box>
        </Box>

        {/* Overview & Status Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 900, mx: 'auto' }}>
              <Grid item xs={12} md={6}>
                {/* Organization Overview Card */}
                <Card sx={{ height: '100%', bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      🏛️ Project Overview
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Project Name</Typography>
                        <Typography variant="body1" fontWeight={600}>{organization.organization_name || 'Unnamed Project'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Project ID</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{organization.unique_id}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Company ID</Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>{organization.company_id || 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Created Date</Typography>
                        <Typography variant="body1" fontWeight={600}>{organization.create_date ? apiService.formatDate(organization.create_date) : 'N/A'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Industry</Typography>
                        <Typography variant="body1" fontWeight={600}>{getIndustryDisplay(organization.industry)}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                {/* Organization Status Card */}
                <Card sx={{ bgcolor: 'background.default', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      📊 Project Status
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Is Demo</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                          <Chip 
                            label={getDemoStatus(organization.demo)} 
                            color={organization.demo ? 'info' : 'default'} 
                            size="small" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Setup Status</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                          <Chip 
                            label={getSetupStatus(organization.need_setup)} 
                            color={organization.need_setup ? 'warning' : 'success'} 
                            size="small" 
                            variant="filled"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
                
                {/* Model Funds Card - Below Project Status */}
                <Box sx={{ mt: 3 }}>
                  <ModelFundsCard 
                    organizationId={organizationId} 
                    organizationName={organization.organization_name}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Description, Token & Company Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <Grid container spacing={5} justifyContent="center" sx={{ maxWidth: 1000, mx: 'auto' }}>
              <Grid item xs={12} md={4}>
                {/* Description */}
                {organization.description && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                      <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Description
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                      {organization.description}
                    </Typography>
                  </Box>
                )}
                
                {/* Token Information */}
                <Box>
                  <Typography variant="h6" gutterBottom fontWeight={600}>
                    🔑 Access Token
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Project Token
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: 'break-all',
                      fontFamily: 'monospace',
                      backgroundColor: 'grey.50',
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      minHeight: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.875rem',
                      lineHeight: 1.4
                    }}
                  >
                    {formatToken(organization.token)}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={8}>
                {/* Associated Company */}
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      🏢 Associated Company
                    </Typography>
                    <Chip 
                      label={company ? '1 company' : 'No companies'}
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  
                  {company ? (
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 3, 
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 3,
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => navigate(`/company/${company.unique_id}`)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          fontSize: '1.1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '70%'
                        }}>
                          {company.name || 'Unnamed Company'}
                        </Typography>
                        <Chip 
                          label={getSubscriptionStatus(company)} 
                          color={getStatusColor(getSubscriptionStatus(company))} 
                          size="small" 
                          variant="filled"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Company ID
                          </Typography>
                          <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all', fontSize: '0.9rem' }}>
                            {company.unique_id}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Subscription Plan
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {company.subscription_plan_id || 'No plan'}
                          </Typography>
                        </Box>
                        
                        {company.paddle_paddle_customer_id && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              Paddle Customer ID
                            </Typography>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                wordBreak: 'break-all',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                backgroundColor: 'grey.50',
                                p: 1,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.200'
                              }}
                            >
                              {company.paddle_paddle_customer_id}
                            </Typography>
                          </Box>
                        )}
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Created Date
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {company.create_date ? apiService.formatDate(company.create_date) : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ) : (
                    <Box sx={{ 
                      textAlign: 'center', 
                      py: 6,
                      px: 3,
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      bgcolor: 'grey.50'
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No companies found for this project
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Session Statistics Section */}
        <SessionStatisticsChart 
          organizationId={organizationId} 
          organizationName={organization.organization_name}
          hideOrganizationSelector={true}
        />

        {/* Project Users Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <OrganizationUsersCard organizationId={organizationId} />
          </CardContent>
        </Card>

        

        {/* Project Rules Section */}
        <Card sx={{ mb: 4, width: '100%' }}>
          <CardContent>
            <OrganizationRulesCard 
              organizationId={organizationId} 
              organizationName={organization.organization_name}
            />
          </CardContent>
        </Card>

        {/* Modals remain unchanged */}
        {/* Project Options Modal */}
        <Modal
          open={organizationOptionsModalOpen}
          onClose={handleOrganizationOptionsModalClose}
          aria-labelledby="organization-options-modal"
          aria-describedby="organization-options-menu"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 300,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden'
          }}>
            <Box sx={{ 
              p: 2, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50'
            }}>
              <Typography variant="h6">
                Project Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {organization?.organization_name}
              </Typography>
            </Box>
            
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                variant="text"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteOrganizationClick}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                Delete Project
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Delete Project Confirmation Modal */}
        <Modal
          open={deleteOrganizationModalOpen}
          onClose={handleDeleteOrganizationModalClose}
          aria-labelledby="delete-organization-modal"
          aria-describedby="delete-organization-confirmation"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}>
            <Typography variant="h6" gutterBottom color="error">
              🗑️ Delete Project
            </Typography>
            
            <Box sx={{ mt: 3 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="medium">
                  This action cannot be undone!
                </Typography>
                <Typography variant="body2">
                  Deleting this project will permanently remove all related data including:
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  <li>All user project roles for this project</li>
                  <li>All dashboard plots and integrations</li>
                  <li>All auto integration status records</li>
                  <li>All rule engine definitions</li>
                  <li>The project itself</li>
                </Box>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To confirm deletion, please enter the exact project name:
                </Typography>
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'grey.50', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  mb: 2
                }}>
                  <Typography variant="body1" fontWeight="medium" color="error">
                    {organization?.organization_name}
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Enter project name to confirm"
                  value={deleteOrganizationConfirmation}
                  onChange={(e) => setDeleteOrganizationConfirmation(e.target.value)}
                  placeholder={`Type "${organization?.organization_name}" to confirm`}
                  disabled={deleteOrganizationLoading}
                  error={deleteOrganizationConfirmation !== '' && deleteOrganizationConfirmation !== organization?.organization_name}
                  helperText={
                    deleteOrganizationConfirmation !== '' && deleteOrganizationConfirmation !== organization?.organization_name
                      ? 'Project name does not match'
                      : ''
                  }
                  sx={{ mt: 1 }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleDeleteOrganizationModalClose}
                  disabled={deleteOrganizationLoading}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="error"
                  onClick={handleDeleteOrganizationConfirm}
                  disabled={deleteOrganizationLoading || deleteOrganizationConfirmation !== organization?.organization_name}
                  sx={{ minWidth: 100 }}
                >
                  {deleteOrganizationLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} color="inherit" />
                      Deleting...
                    </Box>
                  ) : (
                    'Delete Project'
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </Box>
    </Box>
  );
}

export default OrganizationDetail; 